import React, { useState, useCallback } from "react";

import Pluralize from "pluralize";

import referenceSourcesExploreApi from "apis/referenceSourcesExplore";
import {
  Typography,
  Button,
  Table,
  Pagination,
} from "clearsense-ui/components";
import { Plus } from "components/Common/clearsense-ui/icons";
import { useUserState } from "contexts/user";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import hyphenize from "utils/hyphenize";
import { getPermissions } from "utils/roleBasedAccess";

import { CROSSWALK_TABLE } from "../../constants/crosswalkTable";
import DeleteCrosswalk from "../DeleteAlert";

const Crosswalk = ({
  referenceSource,
  fetchReferenceSourceData,
  features,
  title,
}) => {
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [crosswalks, setCrosswalks] = useState({ data: [], count: 0 });

  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteModalOpen(true);
  };

  const { columns } = useColumnHeaderSelector(
    CROSSWALK_TABLE(handleDelete, userPermissions)
  );

  const fetchCrosswalkData = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const crosswalkDirection = title === "Crosswalk In - List" ? "in" : "out";
      const params = new URLSearchParams(window.location.search);
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const {
        data: { crosswalks, crosswalks_count },
      } = await referenceSourcesExploreApi.fetchCrosswalks(
        referenceSource.id,
        currentPage,
        pageSize,
        crosswalkDirection
      );
      setCrosswalks({
        data: crosswalks,
        count: crosswalks_count,
      });
    } catch (error) {
      logger.log(error);
    } finally {
      setIsTableLoading(false);
    }
  }, []);

  const deleteCrosswalk = async () => {
    try {
      setIsTableLoading(true);
      await referenceSourcesExploreApi.deleteCrosswalks(rowToBeDeleted.id);
      fetchCrosswalkData();
      fetchReferenceSourceData();
      setCurrentPage(1);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const { currentPage, pageSize, setCurrentPage, setPageSize } =
    useSearchAndPaginate(fetchCrosswalkData);

  return (
    <>
      <div className="flex justify-start mt-4">
        <div className="flex items-center gap-4 mr-auto">
          <Typography style="large" type="bold">
            {Pluralize(
              title === "Crosswalk In - List"
                ? "Crosswalk In"
                : "Crosswalk Out",
              crosswalks.count,
              true
            )}
          </Typography>
          <Button
            label="Add Filter"
            icon={Plus}
            style="secondary"
            data-cy={`${hyphenize(title)}-add-filter`}
          />
        </div>
        <div className="mr-auto">
          <Typography style="h3" type="semibold">
            {title}
          </Typography>
        </div>
      </div>
      <div className="cs-ui-container cs-ui-container__table mt-4">
        <div className="flex flex-col flex-grow p-2 cs-ui-table-background">
          <Table
            columnData={columns.selected}
            rowData={crosswalks.data}
            fixedHeight
            rowKey={record => record.id}
            isLoading={isTableLoading}
            data-cy="crosswalk-table"
          />
        </div>
      </div>
      <Pagination
        className="px-6 mt-auto"
        totalRecordsCount={crosswalks?.count}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        showResultsPerPage
        setPageSize={setPageSize}
        pageSizeOptionsList={[10, 20, 30]}
      />
      <DeleteCrosswalk
        deleteRecords={deleteCrosswalk}
        entityName="Crosswalk"
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        isDeleteAlertOpen={isDeleteModalOpen}
        setIsDeleteAlertOpen={setIsDeleteModalOpen}
      />
    </>
  );
};

export default Crosswalk;
